import type { NoteTemplateInput } from "@/schema/note-template"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"

import {
  cloneCompanyNoteTemplate,
  createCompanyNoteTemplate,
  destroyCompanyNoteTemplate,
  renderNoteTemplate,
  updateCompanyNoteTemplate,
} from "./api"

export function useCreateCompanyNoteTemplate() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      noteTemplate,
      scope,
      newFiles,
    }: {
      noteTemplate: NoteTemplateInput
      scope?: "company" | "book"
      newFiles?: File[]
    }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return createCompanyNoteTemplate(context, noteTemplate, scope ? { scope } : undefined, newFiles)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["company-note-templates"] })
    },
  })
}

export function useUpdateCompanyNoteTemplate() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      noteTemplate,
      newFiles,
    }: {
      id: string
      noteTemplate: Partial<NoteTemplateInput>
      newFiles?: File[]
    }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return updateCompanyNoteTemplate(context, id, noteTemplate, newFiles)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["company-note-templates"] })
    },
  })
}

export function useDestroyCompanyNoteTemplate() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return destroyCompanyNoteTemplate(context, id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["company-note-templates"] })
    },
  })
}

export const useDeleteCompanyNoteTemplate = useDestroyCompanyNoteTemplate

export function useCloneCompanyNoteTemplate() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, bookId }: { id: string; bookId?: string }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return cloneCompanyNoteTemplate(context, id, bookId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["company-note-templates"] })
    },
  })
}

export function useRenderNoteTemplate() {
  const getContext = useApiContext()

  return useMutation({
    mutationFn: async ({
      noteTemplateId,
      householdId,
      meetingId,
    }: {
      noteTemplateId: string
      householdId?: string
      meetingId?: string
    }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return renderNoteTemplate(context, noteTemplateId, householdId, meetingId)
    },
  })
}
